import {useEffect, useMemo} from 'react'
import {COPILOT_PATH, threadName} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {useSafeChatStateLens} from '@github-ui/copilot-chat/CopilotChatContext'
import {useSafeChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import type {SidebarChat} from '../types/sidebar-chat'

const NO_CHATS: SidebarChat[] = []
// Wrap the Map so the observable compares its reference when threads change.
const selectThreads = (state: CopilotChatState | undefined) => ({threads: state?.threads})
const selectThreadsLoadingState = (state: CopilotChatState | undefined) => state?.threadsLoading?.state
const selectCurrentUserLogin = (state: CopilotChatState | undefined) => state?.currentUserLogin
const selectApiUrl = (state: CopilotChatState | undefined) => state?.apiUrl

export const legacySidebarChatsQueryKey = (currentUserLogin: string, apiUrl: string) =>
  ['legacy-sidebar-chats', currentUserLogin, apiUrl] as const

export function useLegacySidebarChats(enabled: boolean): {chats: SidebarChat[]; isLoading: boolean; isError: boolean} {
  const manager = useSafeChatManager()
  const queryClient = useQueryClient()
  const {threads} = useSafeChatStateLens(selectThreads)
  const threadsLoading = useSafeChatStateLens(selectThreadsLoadingState)
  const currentUserLogin = useSafeChatStateLens(selectCurrentUserLogin)
  const apiUrl = useSafeChatStateLens(selectApiUrl)
  const cacheKey = useMemo(
    () => legacySidebarChatsQueryKey(currentUserLogin ?? '', apiUrl ?? ''),
    [apiUrl, currentUserLogin],
  )
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- This query is an in-memory cache and never fetches.
  const {data: cachedChats} = useQuery<SidebarChat[]>({
    queryKey: cacheKey,
    queryFn: () => NO_CHATS,
    enabled: false,
    gcTime: Infinity,
  })

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Fetch when chat history becomes available.
    if (enabled && manager && threads?.size === 0 && threadsLoading === 'pending') {
      void manager.fetchThreads()
    }
  }, [enabled, manager, threads?.size, threadsLoading])

  const chats = useMemo(() => {
    if (!enabled || !manager || !threads) return NO_CHATS
    return manager.sortAndFilterThreads(threads).map(thread => ({
      taskId: thread.id,
      title: threadName(thread),
      updatedAt: thread.updatedAt,
      latestSessionState: 'unknown',
      resumable: true,
      ...(thread.repoID && thread.repoID > 0 ? {repoId: thread.repoID} : {}),
      legacy: {ssoRequired: Boolean(thread.ssoRequired)},
    }))
  }, [enabled, manager, threads])

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Keep the app-scoped cache aligned with loaded provider state across remounts.
    if (!enabled || !manager || !currentUserLogin || !apiUrl) return

    if (threadsLoading === 'loaded') {
      queryClient.setQueryData(cacheKey, chats)
    } else if (threadsLoading === 'error') {
      queryClient.removeQueries({queryKey: cacheKey, exact: true})
    }
  }, [apiUrl, cacheKey, chats, currentUserLogin, enabled, manager, queryClient, threadsLoading])

  const visibleChats =
    enabled && manager && (threadsLoading === 'pending' || threadsLoading === 'loading') && cachedChats
      ? cachedChats
      : chats

  return useMemo(
    () => ({
      chats: visibleChats,
      isLoading: Boolean(
        enabled &&
        manager &&
        visibleChats.length === 0 &&
        cachedChats === undefined &&
        (threadsLoading === 'pending' || threadsLoading === 'loading'),
      ),
      isError: Boolean(enabled && manager && threadsLoading === 'error'),
    }),
    [cachedChats, enabled, manager, threadsLoading, visibleChats],
  )
}

export function getSidebarChatHref(chat: SidebarChat, fallback: string, searchParams?: URLSearchParams): string {
  const href = chat.legacy ? `${COPILOT_PATH}/c/${chat.taskId}` : fallback
  const sq = isFeatureEnabled('copilot_mission_control_sidebar_filtering') ? searchParams?.get('sq') : undefined
  if (!sq || !href) return href
  const hashIndex = href.indexOf('#')
  const pathAndQuery = hashIndex === -1 ? href : href.slice(0, hashIndex)
  const hash = hashIndex === -1 ? '' : href.slice(hashIndex)
  const queryIndex = pathAndQuery.indexOf('?')
  const params = new URLSearchParams(queryIndex === -1 ? '' : pathAndQuery.slice(queryIndex + 1))
  params.set('sq', sq)
  return `${queryIndex === -1 ? pathAndQuery : pathAndQuery.slice(0, queryIndex)}?${params}${hash}`
}

export function isSidebarChatCurrent(
  chat: SidebarChat,
  {activeChatTaskId, activeThreadId}: {activeChatTaskId?: string; activeThreadId?: string},
): boolean {
  return chat.legacy ? chat.taskId === activeThreadId : chat.taskId === activeChatTaskId
}
